import BlockingScreenExit from 'src/features/app-exit/components/blocking-screen-exit';
// Import order here is load-bearing: `@mui/material/styles` must be evaluated before
// `@mui/material/Box`. Box's module runs `createTheme()` at import time, and Vite's dep
// pre-bundler splits it into a chunk separate from createTheme's — if Box loads first, the
// circular init throws `createTheme_default is not a function` and blanks the whole app.
// Importing styles first initializes createTheme, so keep the linter from reordering these.
/* eslint-disable perfectionist/sort-imports */
import { keyframes } from '@mui/material/styles';
import Box, { BoxProps } from '@mui/material/Box';
/* eslint-enable perfectionist/sort-imports */

import { PLATFORM_INFO } from 'src/config-global';

// ----------------------------------------------------------------------

const logoIn = keyframes`
  0% { opacity: 0; transform: translateY(10px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const markPulse = keyframes`
  0%, 100% { transform: scale(1); filter: drop-shadow(0 18px 34px rgba(0, 167, 111, 0.18)); }
  50% { transform: scale(1.035); filter: drop-shadow(0 22px 42px rgba(0, 167, 111, 0.28)); }
`;

const wordSlide = keyframes`
  0%, 20% { transform: translateY(0); }
  25%, 45% { transform: translateY(-32px); }
  50%, 70% { transform: translateY(-64px); }
  75%, 95% { transform: translateY(-96px); }
  100% { transform: translateY(0); }
`;

const progressFill = keyframes`
  0% { width: 8%; }
  100% { width: 100%; }
`;

const sheen = keyframes`
  0% { transform: translateX(-130%); }
  100% { transform: translateX(360%); }
`;

const statusWords = ['Pay', 'Secure', 'Fast', 'Manage'];
const supportPhone = PLATFORM_INFO.TECHNICAL_SUPPORT_PHONE;
const supportEmail = PLATFORM_INFO.SUPPORT_EMAIL;
const supportPhoneDigits = supportPhone.replace(/\D/g, '');
const supportPhoneHref = supportPhone.trim().startsWith('+')
  ? `tel:+${supportPhoneDigits}`
  : `tel:${supportPhoneDigits}`;

// ----------------------------------------------------------------------

function PointcraftMark({ sx, ...other }: BoxProps) {
  return (
    <Box
      component="svg"
      viewBox="0 0 1013 1013"
      xmlns="http://www.w3.org/2000/svg"
      sx={{ display: 'block', ...sx }}
      {...other}
    >
      <path
        d="M764.474 453.522H619.133C599.215 411.295 556.245 381.994 506.5 381.994C437.849 381.994 381.987 437.85 381.987 506.512C381.987 575.173 437.849 631.035 506.5 631.035C557.964 631.035 602.249 599.657 621.206 555.033H726.155H1010.67C1012.24 539.058 1013 522.881 1013 506.502C1013 488.606 1012.09 470.963 1010.21 453.522H764.474Z"
        fill="#5CC474"
      />
      <path
        d="M506.5 911.883C283.104 911.883 101.35 730.275 101.117 506.92V506.505H101.107C101.107 506.5 101.107 506.5 101.107 506.495C101.107 282.998 282.953 101.107 506.5 101.107C675.349 101.107 820.285 204.883 881.152 351.953H988.831C923.364 148.041 731.918 -0.000244141 506.5 -0.000244141C227.243 -0.000244141 0 227.238 0 506.495C0 506.778 0.0103674 507.061 0.0103674 507.344V1013H101.117V809.821C193.574 933.095 340.887 1012.99 506.5 1012.99C733.586 1012.99 926.195 862.846 990.297 656.588H883.073C823.319 806.024 677.068 911.883 506.5 911.883Z"
        fill="#81A1CD"
      />
    </Box>
  );
}

// ----------------------------------------------------------------------

interface SplashScreenProps extends BoxProps {
  message?: string;
  allowAppExit?: boolean;
}

export default function SplashScreen({ message, allowAppExit = false, sx, ...other }: SplashScreenProps) {
  const renderedWords = message ? [message] : statusWords;

  return (
    <Box
      role="status"
      aria-label={message || 'Loading PointCraft'}
      sx={{
        top: 0,
        left: 0,
        right: 0,
        width: 1,
        bottom: 0,
        height: 1,
        minHeight: '100vh',
        zIndex: 9998,
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 3, sm: 4 },
        pt: 'max(28px, env(safe-area-inset-top))',
        pb: 'calc(28px + env(safe-area-inset-bottom))',
        overflow: 'hidden',
        fontFamily: "'Public Sans', sans-serif",
        bgcolor: '#161C24',
        background:
          'radial-gradient(620px 420px at 50% 35%, rgba(0, 167, 111, 0.1) 0%, rgba(22, 28, 36, 0) 58%), #161C24',
        ...sx,
      }}
      {...other}
    >
      {allowAppExit && <BlockingScreenExit placement="overlay" />}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          pt: 4,
          pb: 3,
          animation: `${logoIn} 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both`,
        }}
      >
        <Box
          sx={{
            width: 112,
            height: 112,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${markPulse} 2.4s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        >
          <PointcraftMark sx={{ width: 88, height: 88 }} />
        </Box>

        <Box
          component="h1"
          aria-hidden="true"
          sx={{
            m: 0,
            mt: 2,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            fontSize: { xs: 30, sm: 34 },
            lineHeight: 1,
            fontFamily: "'Barlow', 'Public Sans', sans-serif",
            fontWeight: 800,
            letterSpacing: 0,
          }}
        >
          <Box component="span" sx={{ color: '#81A1CD' }}>
            Point
          </Box>
          <Box component="span" sx={{ color: '#5CC474' }}>
            Craft
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3.5,
            height: 32,
            minWidth: 128,
            overflow: 'hidden',
            color: '#919EAB',
            fontSize: 15,
            fontWeight: 700,
            lineHeight: '32px',
            letterSpacing: '1.4px',
            textTransform: 'uppercase',
          }}
        >
          <Box
            sx={{
              animation:
                renderedWords.length > 1
                  ? `${wordSlide} ${renderedWords.length * 1.6}s cubic-bezier(0.4, 0, 0.2, 1) infinite`
                  : 'none',
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          >
            {renderedWords.map((word) => (
              <Box key={word} sx={{ height: 32 }}>
                {word}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: 1,
          maxWidth: 360,
          flexShrink: 0,
          animation: `${logoIn} 600ms cubic-bezier(0.2, 0.7, 0.2, 1) 240ms both`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.375,
          }}
        >
          <Box
            component="span"
            sx={{
              gap: 0.75,
              display: 'inline-flex',
              alignItems: 'center',
              color: '#637381',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
            }}
          >
            <Box
              component="svg"
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              sx={{ flexShrink: 0 }}
            >
              <path
                d="M12 3 5 6v5c0 4 3 7.5 7 8.5 4-1 7-4.5 7-8.5V6l-7-3Z"
                stroke="#5CC474"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="m9 12 2 2 4-4"
                stroke="#5CC474"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Box>
            Secured
          </Box>
        </Box>

        <Box
          sx={{
            width: 1,
            height: 5,
            borderRadius: '3px',
            bgcolor: 'rgba(145, 158, 171, 0.16)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: 1,
              borderRadius: '3px',
              overflow: 'hidden',
              background: 'linear-gradient(90deg, #007867, #00A76F 55%, #5BE49B)',
              animation: `${progressFill} 3000ms cubic-bezier(0.3, 0.5, 0.4, 1) forwards`,
              '@media (prefers-reduced-motion: reduce)': {
                width: '88%',
                animation: 'none',
              },
            }}
          >
            <Box
              component="span"
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
                animation: `${sheen} 1250ms linear infinite`,
                '@media (prefers-reduced-motion: reduce)': {
                  display: 'none',
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.25,
            pt: 1.875,
            gap: 1.125,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderTop: '1px solid rgba(145, 158, 171, 0.12)',
          }}
        >
          <Box
            sx={{
              color: '#454F5B',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
            }}
          >
            Questions? Our team is here 24/7
          </Box>

          <Box
            component="a"
            href={supportPhoneHref}
            sx={{
              gap: 1,
              display: 'inline-flex',
              alignItems: 'center',
              color: '#919EAB',
              fontSize: 13.5,
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': { color: '#5CC474' },
            }}
          >
            <Box
              component="svg"
              width={15}
              height={15}
              viewBox="0 0 24 24"
              fill="#5CC474"
              sx={{ flexShrink: 0 }}
            >
              <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.4 21 3 13.6 3 4.5c0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.3 1l-2.1 1.7Z" />
            </Box>
            {supportPhone}
          </Box>

          <Box
            component="a"
            href={`mailto:${supportEmail}`}
            sx={{
              gap: 1,
              display: 'inline-flex',
              alignItems: 'center',
              color: '#919EAB',
              fontSize: 13.5,
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': { color: '#81A1CD' },
            }}
          >
            <Box
              component="svg"
              width={15}
              height={15}
              viewBox="0 0 24 24"
              fill="#81A1CD"
              sx={{ flexShrink: 0 }}
            >
              <path d="M4 5h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2Zm0 2v.4l8 5 8-5V7H4Zm16 2.8-6.9 4.3c-.7.4-1.5.4-2.2 0L4 9.8V17h16V9.8Z" />
            </Box>
            {supportEmail}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
